import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _inherits from "@babel/runtime/helpers/inherits";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _extends from "@babel/runtime/helpers/extends";

function _createSuper(Derived) {
  function isNativeReflectConstruct() {
    if (typeof Reflect === "undefined" || !Reflect.construct) return false;
    if (Reflect.construct.sham) return false;
    if (typeof Proxy === "function") return true;

    try {
      Date.prototype.toString.call(Reflect.construct(Date, [], function () {}));
      return true;
    } catch (e) {
      return false;
    }
  }

  return function () {
    var Super = _getPrototypeOf(Derived),
        result;

    if (isNativeReflectConstruct()) {
      var NewTarget = _getPrototypeOf(this).constructor;

      result = Reflect.construct(Super, arguments, NewTarget);
    } else {
      result = Super.apply(this, arguments);
    }

    return _possibleConstructorReturn(this, result);
  };
}

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import uuid from '../utils/uuid';
import { withStyles } from '../theme';
import { isolateMixin, focusSourceMixin } from '../utils/mixins';
import { RadioButtonContext } from './context';
import { subscribeFocusEvents } from '../utils/focus-source';
subscribeFocusEvents();

var isSimpleType = function isSimpleType(value) {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null || value === undefined;
};

var styles = function styles(theme) {
  return {
    root: {
      extend: isolateMixin,
      fontFamily: theme.fontFamily,
      fontSize: theme.radio.fontSize,
      fontWeight: theme.radio.fontWeight,
      display: 'flex',
      width: '100%',
      cursor: 'pointer',
      position: 'relative',
      userSelect: 'none',
      '&:not(:last-child)': {
        marginBottom: theme.radio.marginBottom
      }
    },
    isDisabled: {
      pointerEvents: 'none',
      cursor: 'not-allowed'
    },
    regular: _extends({
      color: theme.radio.types.regular.colors.default.text,
      '&$isDisabled': {
        color: theme.radio.types.regular.colors.disabled.text
      },
      '& $fake': {
        borderColor: theme.radio.types.regular.colors.default.dotBorder,
        background: theme.radio.types.regular.colors.default.dotBackground,
        color: theme.radio.types.regular.colors.default.dot
      },
      '&$isChecked $fake': {
        borderColor: theme.radio.types.regular.colors.checked.dotBorder,
        background: theme.radio.types.regular.colors.checked.dotBackground
      },
      '&$isEnabled:hover $fake': {
        borderColor: theme.radio.types.regular.colors.hover.dotBorder,
        color: theme.radio.types.regular.colors.hover.dot
      }
    }, focusSourceMixin('other', '& $real:focus + $fake', {
      borderColor: theme.radio.types.regular.colors.focus.dotBorder
    }), {
      '&$isEnabled:active $fake': {
        borderColor: theme.radio.types.regular.colors.active.dotBorder,
        background: theme.radio.types.regular.colors.active.dotBackground,
        color: theme.radio.types.regular.colors.active.dot
      },
      '&$isDisabled $fake': {
        borderColor: theme.radio.types.regular.colors.disabled.dotBorder,
        color: theme.radio.types.regular.colors.disabled.dot,
        background: theme.radio.types.regular.colors.disabled.dotBackground
      }
    }),
    awesome: _extends({
      color: theme.radio.types.awesome.colors.default.text,
      '&$isDisabled': {
        color: theme.radio.types.awesome.colors.disabled.text
      },
      '& $fake': {
        borderColor: theme.radio.types.awesome.colors.default.dotBorder,
        background: theme.radio.types.awesome.colors.default.dotBackground,
        color: theme.radio.types.awesome.colors.default.dot
      },
      '&$isChecked $fake': {
        borderColor: theme.radio.types.awesome.colors.checked.dotBorder,
        background: theme.radio.types.awesome.colors.checked.dotBackground
      },
      '&$isEnabled:hover $fake': {
        borderColor: theme.radio.types.awesome.colors.hover.dotBorder,
        color: theme.radio.types.awesome.colors.hover.dot,
        background: theme.radio.types.awesome.colors.hover.dotBackground
      },
      '&$isEnabled$isChecked:hover $fake': {
        borderColor: 'transparent',
        background: theme.radio.types.awesome.colors.checkedHover.dotBackground
      },
      '&$isEnabled$isChecked:active $fake': {
        borderColor: 'transparent',
        background: theme.radio.types.awesome.colors.checkedActive.dotBackground
      }
    }, focusSourceMixin('other', '& $real:focus + $fake', {
      borderColor: theme.radio.types.awesome.colors.focus.dotBorder
    }), {
      '&$isDisabled $fake': {
        borderColor: theme.radio.types.awesome.colors.disabled.dotBorder,
        color: theme.radio.types.awesome.colors.disabled.dot,
        background: theme.radio.types.awesome.colors.disabled.dotBackground
      },
      '&$isDisabled$isChecked $fake': {
        borderColor: theme.radio.types.awesome.colors.checkedDisabled.dotBorder,
        color: theme.radio.types.awesome.colors.checkedDisabled.dot,
        background: theme.radio.types.awesome.colors.checkedDisabled.dotBackground
      }
    }),
    real: {
      fontFamily: theme.fontFamily,
      position: 'absolute',
      opacity: '0',
      appearance: 'none',
      pointerEvents: 'none'
    },
    fake: _extends({
      flex: 'none',
      display: 'inline-block',
      verticalAlign: 'middle',
      position: 'relative',
      boxSizing: 'border-box',
      borderRadius: '50%',
      width: theme.radio.sizes.size,
      height: theme.radio.sizes.size,
      border: '1px solid',
      marginTop: Math.ceil((theme.radio.lineHeight - theme.radio.sizes.size) / 2),
      transitionDuration: theme.radio.animationDuration,
      transitionProperty: 'border-color, background-color, color',
      '&:after': {
        boxSizing: 'border-box',
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        margin: 'auto',
        width: theme.radio.sizes.dotSize,
        height: theme.radio.sizes.dotSize,
        transform: 'scale(0.5, 0.5)',
        opacity: 0,
        transitionDuration: 'inherit',
        transitionProperty: 'opacity, transform, background-color',
        background: 'currentColor',
        borderRadius: '50%'
      }
    }, theme.radio.outline && {
      '&:before': {
        content: '""',
        display: 'block',
        position: 'absolute',
        width: theme.radio.sizes.size,
        height: theme.radio.sizes.size,
        borderRadius: '50%',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        border: "solid ".concat(theme.radio.outline.color),
        borderWidth: 0,
        transitionDuration: 'inherit',
        transitionProperty: 'border-width'
      },
      '$isEnabled:active &:before': {
        borderWidth: theme.radio.outline.width
      }
    }, {
      '$isChecked &:after': {
        transform: 'scale(1, 1)',
        opacity: 1
      },
      '$labelright &': {
        marginRight: theme.radio.sizes.labelMargin
      },
      '$labelleft &': {
        marginLeft: theme.radio.sizes.labelMargin
      }
    }),
    label: {
      display: 'inline-block',
      verticalAlign: 'middle',
      lineHeight: theme.radio.lineHeight + 'px',
      '$labelleft &': {
        order: -1,
        marginRight: 'auto'
      }
    },
    isChecked: {},
    isEnabled: {},
    labelleft: {},
    labelright: {}
  };
};

var RadioButton = /*#__PURE__*/function (_PureComponent) {
  _inherits(RadioButton, _PureComponent);

  var _super = _createSuper(RadioButton);

  function RadioButton(props) {
    var _this;

    _classCallCheck(this, RadioButton);

    _this = _super.call(this, props);

    _this.onUpdateValue = function () {
      _this.forceUpdate();
    };

    _this.onChange = function (event) {
      _this.context.events.emit('newValue', event, _this.props.value); // оставляем для обратной совместимости
      // eslint-disable-next-line react/prop-types


      if (_this.props.onChange) _this.props.onChange(event, _this.inputValue);
    };

    _this.setInputValue(_this.props.value);

    return _this;
  }

  _createClass(RadioButton, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      this.context.events.on('updateValue', this.onUpdateValue);
      if (this.input) this.input.checked = this.isChecked;
    }
  }, {
    key: "componentDidUpdate",
    value: function componentDidUpdate(prevProps) {
      if (this.props.value !== prevProps.value) this.setInputValue(this.props.value);
      if (this.input) this.input.checked = this.isChecked;
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      this.context.events.removeListener('updateValue', this.onUpdateValue);
    }
  }, {
    key: "setInputValue",
    value: function setInputValue(value) {
      this.inputValue = value;
      this.stringValue = isSimpleType(value) ? String(value) : uuid();
    }
  }, {
    key: "render",
    value: function render() {
      var _this2 = this;

      var _this$props = this.props,
          children = _this$props.children,
          labelPosition = _this$props.labelPosition,
          disabled = _this$props.disabled,
          className = _this$props.className,
          radioClassName = _this$props.radioClassName,
          labelClassName = _this$props.labelClassName,
          labelStyle = _this$props.labelStyle,
          variation = _this$props.variation,
          classes = _this$props.classes,
          onFocus = _this$props.onFocus,
          onBlur = _this$props.onBlur,
          theme = _this$props.theme,
          value = _this$props.value,
          onChange = _this$props.onChange,
          other = _objectWithoutProperties(_this$props, ["children", "labelPosition", "disabled", "className", "radioClassName", "labelClassName", "labelStyle", "variation", "classes", "onFocus", "onBlur", "theme", "value", "onChange"]);

      var rootClassName = classnames(className, classes.root, classes[variation], classes["label".concat(labelPosition)], disabled ? classes.isDisabled : classes.isEnabled, this.isChecked && classes.isChecked);
      return React.createElement("label", _extends({
        className: rootClassName
      }, other), React.createElement("input", {
        className: classes.real,
        type: "radio",
        ref: function ref(input) {
          _this2.input = input;
        },
        name: this.context.getName(),
        value: this.stringValue,
        disabled: disabled,
        onChange: this.onChange,
        onFocus: onFocus,
        onBlur: onBlur
      }), React.createElement("span", {
        className: classnames(radioClassName, classes.fake)
      }), React.createElement("span", {
        className: classnames(labelClassName, classes.label),
        style: labelStyle
      }, children));
    }
  }, {
    key: "isChecked",
    get: function get() {
      return this.inputValue === this.context.getValue();
    }
  }]);

  return RadioButton;
}(PureComponent);

RadioButton.defaultProps = {
  labelPosition: 'right',
  value: null,
  disabled: false,
  style: {},
  labelStyle: {},
  variation: 'regular'
};
RadioButton.contextType = RadioButtonContext;
RadioButton.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Выбранное значение radioButton
   */
  value: PropTypes.any.isRequired,

  /**
   * Если true, RadioButton задизэйблен
   */
  disabled: PropTypes.bool,

  /**
   * Css-класс root-компонента(radioButton)
   */
  className: PropTypes.string,

  /**
   * Css-класс span-radio
   */
  radioClassName: PropTypes.string,

  /**
   * Css-класс span-label
   */
  labelClassName: PropTypes.string,

  /**
   * Переопределение стандартных стилей компонента radioButton
   */
  style: PropTypes.object,

  /**
   * Переопределение стандартных стилей label
   */
  labelStyle: PropTypes.object,

  /**
   * Позиция label - либо слева, либо справа
   */
  labelPosition: PropTypes.oneOf(['left', 'right']),

  /**
   * Разновидность инпута
   */
  variation: PropTypes.oneOf(['regular', 'awesome']),

  /**
   * Контент поля
   */
  children: PropTypes.node
} : {};
export default withStyles(styles, {
  name: 'RadioButton'
})(RadioButton);